import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useDataRerunsActions } from 'redux/actions/internal/DataReruns'
import {
  DataRerunDetail,
  DataRerunSummary,
  selectActiveReruns,
  selectActiveRerunsFetchStatus,
  selectInternalDataReruns,
  selectRerun,
  selectRerunFetchStatus,
  selectReruns,
  selectRerunsFetchStatus
} from 'redux/selectors/internal/DataReruns'
import { ApplicationState } from 'redux/Stores/types'
import { EReduxState, getBooleanStates } from 'redux/utils/State'

type TFetchState = {
  error: boolean
  loading: boolean
  /** Fetches again even if loaded, for polling and after a change. */
  refetch: () => void
}

/** The candidates and mutation state the rerun dialog and tab read. */
export const useInternalDataReruns = () => {
  return useSelector(selectInternalDataReruns)
}

/** Reruns that have not finished, newest first, fetched on mount. */
export const useActiveReruns = (): TFetchState & {
  activeReruns: DataRerunSummary[]
} => {
  const { tryGetActiveReruns, tryRefetchActiveReruns } = useDataRerunsActions()

  const activeReruns = useSelector(selectActiveReruns)
  const fetchStatus = useSelector(selectActiveRerunsFetchStatus)

  useEffect(() => {
    tryGetActiveReruns()
  }, [tryGetActiveReruns])

  return {
    activeReruns,
    error: fetchStatus === EReduxState.ERROR,
    loading: getBooleanStates(fetchStatus).loading,
    refetch: tryRefetchActiveReruns
  }
}

/**
 * Every rerun, newest first, null until fetched. Fetched each time `enabled`
 * turns true; `refetch` does nothing while disabled.
 */
export const useReruns = ({
  enabled
}: {
  enabled: boolean
}): TFetchState & { reruns: DataRerunSummary[] | null } => {
  const { tryGetReruns, tryRefetchReruns } = useDataRerunsActions()

  const reruns = useSelector(selectReruns)
  const fetchStatus = useSelector(selectRerunsFetchStatus)

  useEffect(() => {
    if (enabled) {
      tryGetReruns()
    }
  }, [enabled, tryGetReruns])

  // stable so a poll interval depending on it is not restarted every render
  const refetch = useCallback(() => {
    if (enabled) {
      tryRefetchReruns()
    }
  }, [enabled, tryRefetchReruns])

  return {
    error: fetchStatus === EReduxState.ERROR,
    loading: getBooleanStates(fetchStatus).loading,
    refetch,
    reruns
  }
}

/** One rerun with its customers, fetched while `id` is set. */
export const useRerun = (
  id: string | null
): TFetchState & { rerun: DataRerunDetail | null } => {
  const { tryGetRerun, tryRefetchRerun } = useDataRerunsActions()

  const rerun = useSelector((state: ApplicationState) => selectRerun(state, id))
  const fetchStatus = useSelector((state: ApplicationState) =>
    selectRerunFetchStatus(state, id)
  )

  useEffect(() => {
    if (id) {
      tryGetRerun(id)
    }
  }, [id, tryGetRerun])

  // stable so a poll interval depending on it is not restarted every render
  const refetch = useCallback(() => {
    if (id) {
      tryRefetchRerun(id)
    }
  }, [id, tryRefetchRerun])

  return {
    error: fetchStatus === EReduxState.ERROR,
    loading: id !== null && getBooleanStates(fetchStatus).loading,
    refetch,
    rerun
  }
}
